import React, { useState } from "react";

import ARTICLE_CONFIG from "../../../data/articleConfig";
import "./artifacts.css";

const QUESTIONS = [
	{
		dimension: "Ownership",
		q: "Is there a single named owner accountable for each AI system in production?",
	},
	{
		dimension: "Ownership",
		q: "Before deploying, do you define who reviews the AI output and signs off?",
	},
	{
		dimension: "Validation",
		q: "Does an independent reviewer check that outputs do what the team assumes?",
	},
	{
		dimension: "Validation",
		q: "Do you have a written, repeatable test process for the model or agent?",
	},
	{
		dimension: "Monitoring",
		q: "Can you detect within hours when an AI system starts behaving differently?",
	},
	{
		dimension: "Monitoring",
		q: "Is there a rollback or kill switch if an AI system degrades in production?",
	},
	{
		dimension: "Governance",
		q: "Can you explain any single AI decision to a regulator or a customer?",
	},
	{
		dimension: "Governance",
		q: "Is AI risk reviewed on a fixed cadence, not just once at launch?",
	},
];

const OPTIONS = [
	{ label: "No", score: 0 },
	{ label: "Partly", score: 1 },
	{ label: "Yes", score: 2 },
];

function tierFor(percent) {
	if (percent >= 90) return { name: "Mature", color: "#2f855a" };
	if (percent >= 70) return { name: "Managed", color: "#3182ce" };
	if (percent >= 40) return { name: "Developing", color: "#dd6b20" };
	return { name: "Early", color: "#c53030" };
}

const AIReadinessScorecard = () => {
	const [answers, setAnswers] = useState({});

	const answered = Object.keys(answers).length;
	const complete = answered === QUESTIONS.length;
	const score = Object.values(answers).reduce((a, b) => a + b, 0);
	const max = QUESTIONS.length * 2;
	const percent = complete ? Math.round((score / max) * 100) : 0;
	const tier = tierFor(percent);

	const gaps = complete
		? [
				...new Set(
					QUESTIONS.filter((q, i) => answers[i] === 0).map(
						(q) => q.dimension
					)
				),
		  ]
		: [];

	const buttondownAction = `https://buttondown.email/api/emails/embed-subscribe/${ARTICLE_CONFIG.buttondownUsername}`;

	return (
		<div className="artifact-card">
			<div className="artifact-kicker">Interactive · Free tool</div>
			<div className="artifact-title">AI Readiness Scorecard</div>
			<div className="artifact-intro">
				Eight questions across ownership, validation, monitoring, and
				governance. Answer honestly for one AI system you run today.
			</div>

			{QUESTIONS.map((item, i) => (
				<div className="artifact-question" key={i}>
					<div className="artifact-q-text">
						{i + 1}. {item.q}
					</div>
					<div className="artifact-options">
						{OPTIONS.map((opt) => (
							<div
								key={opt.label}
								className={
									"artifact-option" +
									(answers[i] === opt.score
										? " selected"
										: "")
								}
								onClick={() =>
									setAnswers((prev) => ({
										...prev,
										[i]: opt.score,
									}))
								}
							>
								{opt.label}
							</div>
						))}
					</div>
				</div>
			))}

			{!complete && (
				<div className="artifact-readout">
					{answered} of {QUESTIONS.length} answered.
				</div>
			)}

			{complete && (
				<div className="artifact-result">
					<div className="artifact-score-row">
						<span className="artifact-score">{percent}</span>
						<span
							className="artifact-tier"
							style={{ color: tier.color }}
						>
							{tier.name}
						</span>
					</div>
					<div className="artifact-bar">
						<div
							className="artifact-bar-fill"
							style={{
								width: `${percent}%`,
								background: tier.color,
							}}
						/>
					</div>
					{gaps.length > 0 ? (
						<div className="artifact-readout">
							Your weakest areas, where you answered "No":
							<ul className="artifact-gaps">
								{gaps.map((g) => (
									<li key={g}>{g}</li>
								))}
							</ul>
						</div>
					) : (
						<div className="artifact-readout">
							No outright gaps. Push the "Partly" answers to
							"Yes" and you are in strong shape.
						</div>
					)}

					<div className="artifact-capture">
						<div className="artifact-capture-text">
							Get the full breakdown and a one-page improvement
							checklist by email.
						</div>
						<form
							className="artifact-capture-form"
							action={buttondownAction}
							method="post"
							target="popupwindow"
							onSubmit={() =>
								window.open(
									`https://buttondown.email/${ARTICLE_CONFIG.buttondownUsername}`,
									"popupwindow"
								)
							}
						>
							<input
								className="artifact-input"
								style={{ width: "220px", textAlign: "left" }}
								type="email"
								name="email"
								placeholder="you@company.com"
								aria-label="Email address"
								required
							/>
							<button className="artifact-btn" type="submit">
								Send my checklist
							</button>
						</form>
					</div>
				</div>
			)}
		</div>
	);
};

export default AIReadinessScorecard;
